import React, { useEffect, useState, useRef } from 'react';

import ReactToPrint from 'react-to-print';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, ResponsiveContainer } from 'recharts';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
  fetchCofogs,
  fetchAllLocations,
  fetchDataTables,
  fetchGraphNodes
} from "../../utils/api/sliceAndDice";

import Nav from '../../common/components/Nav';
import Footer from '../../common/components/Footer';
import CofogsAccordion from '../../common/components/CofogsAccordion';
import AccordionComponent from '../../common/components/AccordionComponent';
import TopicsAccordion from '../../common/components/TopicsAccordion';
import { errorHandler } from "../../utils/helpers/errorHandler";
import "./index.css";

import 'react-accessible-accordion/dist/fancy-example.css';

const SliceAndDice = () => {
  const componentRef = useRef();

  const [cofogs, setCofogs] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [dataTables, setDataTables] = useState({});
  const [years, setYears] = useState([]);
  const [queryForVariables, setQueryForVariables] = useState('');
  const [graph, setGraph] = useState([]);
  const [loc, setLoc] = useState([]);
  const [selectedYear, setSelectedYear] = useState([]);
  const [locationIsLoading, setLocationIsLoading] = useState(false);
  const [dataTableIsLoading, setDataTableIsLoading] = useState(false);

  // Multi-table selection states
  const [selectedTables, setSelectedTables] = useState([]);
  const [variablesByTable, setVariablesByTable] = useState({});

  const generateRandomColor = () => `#${Math.floor(Math.random() * 16777215).toString(16)}`;

  const handleFetchCofogs = async () => {
    try {
      const { data } = await fetchCofogs();
      setCofogs(data);
    } catch (error) {
      errorHandler(error, toast);
    }
  };

  const handleFetchAllLocations = async (query) => {
    setLocationIsLoading(true);
    try {
      const { data: { locations } } = await fetchAllLocations(query);
      setAllLocations(locations);
      setLocationIsLoading(false);
    } catch (error) {
      setLocationIsLoading(false);
      errorHandler(error, toast);
    }
  };

  const handleFetchDataTables = async (query) => {
    setDataTableIsLoading(true);
    try {
      const { data: { data_table } } = await fetchDataTables(query);
      setDataTables(data_table);
      setDataTableIsLoading(false);
    } catch (error) {
      setDataTableIsLoading(false);
      errorHandler(error, toast);
    }
  };

  useEffect(() => {
    fetchDataForSelectedTables();
  }, [selectedTables, variablesByTable, queryForVariables]);

  useEffect(() => {
    handleFetchCofogs();
  }, []);

  const handlePopulateTopics = async (event) => {
    try {
      const checkedCofogs = cofogs.filter(data => document.getElementById(`${data.cofog}`).checked);
      let query = '';

      setDataTables([]);
      setYears([]);
      setAllLocations([]);

      cofogs.forEach(data => {
        if (document.getElementById(`${data.cofog}`)) {
          document.getElementById(`${data.cofog}`).checked = document.getElementById(`${data.cofog}`).checked;
        }
        if (data.organisations.length) {
          data.organisations.forEach(organisation => {
            if (document.getElementById(`${organisation}`)) {
              document.getElementById(`${organisation}`).checked = document.getElementById(`${data.cofog}`).checked;
            }
          });
        }
      });

      query = checkedCofogs.map(data => `cofog=${data.cofog}`).join('&');

      const organisationQuery = cofogs.map(data => {
        if (data.organisations.length) {
          return data.organisations.filter(organisation => document.getElementById(`${organisation}`).checked).join('$');
        }
        return '';
      }).filter(value => value.length).join('$');

      setQueryForVariables(`${query}&organisation=${organisationQuery}`);
      handleFetchAllLocations(`${query}&organisation=${organisationQuery}`);
    } catch (error) {
      errorHandler(error, toast);
    }
  };

  const handlePopulateVariables = (location) => {
    setLoc(prevLoc => {
      if (prevLoc.includes(location)) {
        return prevLoc.filter(l => l !== location);
      } else {
        return [...prevLoc, location];
      }
    });

    let locationQuery = [...loc, location].map(value => `location=${value}`).join('&');
    setQueryForVariables(`${queryForVariables}&${locationQuery}`);
    handleFetchDataTables(`${queryForVariables}&${locationQuery}`);
  };

  const handleFetchTableNodes = async (table) => {
    setYears([]);
    setSelectedYear([]);
    
    setSelectedTables(prev => {
      const newTables = prev.includes(table) 
        ? prev.filter(t => t !== table) 
        : [...prev, table];
      return newTables;
    });

    setVariablesByTable(prev => {
      const newState = {
        ...prev,
        [table]: prev[table] || dataTables[table]
      };
      
      setTimeout(() => fetchDataForSelectedTables(), 0);
      return newState;
    });
  };

  const fetchDataForSelectedTables = async () => {
    try {
      if (selectedTables.length === 0) {
        setGraph([]);
        setYears([]);
        return;
      }

      const tableQueries = selectedTables.map(t => `data_tables=${t}`).join('&');
      const activeVariables = Object.entries(variablesByTable)
        .filter(([t]) => selectedTables.includes(t))
        .flatMap(([t, vars]) => vars);
      
      if (activeVariables.length === 0) {
        setGraph([]);
        setYears([]);
        return;
      }

      const query = `${queryForVariables}&${tableQueries}&variables=${activeVariables.join(',')}`;
      const { data } = await fetchGraphNodes(query);

      const yearsList = Array.from(
        new Set(Object.values(data).flatMap(v => Object.keys(v)))
      ).sort((a, b) => a - b);
      
      const graphData = yearsList.map(year => {
        const vertice = { name: +year };
        for (const key in data) {
          if (data[key][year]) vertice[key] = data[key][year];
        }
        return vertice;
      });

      setYears(yearsList);
      setGraph(graphData);
    } catch (error) {
      errorHandler(error, toast);
    }
  };

  const handleSelectOrganisation = async (event, cofog) => {
    setAllLocations([]);
    setDataTables([]);
    setYears([]);
    let query = '';

    if (document.getElementById(`${event.target.value}`)) {
      document.getElementById(`${event.target.value}`).checked = document.getElementById(`${event.target.value}`).checked;
    }
    if (document.getElementById(`${cofog}`)) {
      document.getElementById(`${cofog}`).checked = true;
    }

    const checkedCofogs = cofogs.filter(data => document.getElementById(`${data.cofog}`).checked);
    query = checkedCofogs.map(data => `cofog=${data.cofog}`).join('&');

    const organisationQuery = cofogs.map(data => {
      if (data.organisations.length) {
        return data.organisations.filter(organisation => document.getElementById(`${organisation}`).checked).join('$');
      }
      return '';
    }).filter(value => value.length).join('$');

    setQueryForVariables(`${query}&organisation=${organisationQuery}`);
    handleFetchAllLocations(`${query}&organisation=${organisationQuery}`);
  };

  const handleFetchTableNodeForVariables = async (table, variable) => {
    setSelectedYear([]);
    
    setVariablesByTable(prev => {
      const currentVars = prev[table] || [];
      const newVars = currentVars.includes(variable)
        ? currentVars.filter(v => v !== variable)
        : [...currentVars, variable];
      
      const newState = {
        ...prev,
        [table]: newVars
      };
      
      setTimeout(() => fetchDataForSelectedTables(), 0);
      return newState;
    });
  };

  const handleSelectYear = async (year) => {
    try {
      if (selectedYear.includes(+year)) {
        const newYears = selectedYear.filter(y => +y !== +year);
        setSelectedYear(newYears);
        
        if (newYears.length === 0) {
          fetchDataForSelectedTables();
        } else {
          const yearlyGraph = graph.filter(value => newYears.includes(+value.name));
          setGraph(yearlyGraph);
        }
      } else {
        const newYears = [...selectedYear, +year];
        setSelectedYear(newYears);
        const yearlyGraph = graph.filter(value => newYears.includes(+value.name));
        setGraph(yearlyGraph);
      }
    } catch (error) {
      errorHandler(error, toast);
    }
  };

  const handleDownloadExcel = () => {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const ws = XLSX.utils.json_to_sheet(graph);
    const wb = { Sheets: { 'data': ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, `${selectedTables.join('_') || 'data'}_${Date.now()}${fileExtension}`);
  };

  return (
    <div
      className="d-flex flex-column min-vh-100"
      style={{
        backgroundColor: "#F8FAFC",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* Page Header */}
      <header className="bg-white border-bottom border-light">
        <Nav />
        <div className="py-5" style={{ backgroundColor: "#F1F5F9" }}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8 text-center">
                <span
                  className="badge rounded-pill px-3 py-2 text-uppercase mb-3 fw-semibold"
                  style={{
                    backgroundColor: "rgba(10, 61, 46, 0.08)",
                    color: "#0A3D2E",
                    fontSize: "12px",
                    letterSpacing: "0.8px",
                  }}
                >
                  Statistical Intelligence
                </span>
                <h1 className="fw-bold text-dark display-6 mb-3" style={{ letterSpacing: "-0.5px" }}>
                  Slice & Dice Data
                </h1>
                <p className="text-secondary fs-6 lh-relaxed mb-0">
                  Access multidimensional statistical data tables across health, education, and economic sectors in Lagos State. Derive custom analytics and export data directly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Control Panel & Visualizations */}
      <section className="flex-grow-1 py-5">
        <div className="container">
          
          {/* Instructions Notice */}
          <div className="alert border-0 shadow-sm rounded-4 bg-white p-4 mb-4">
            <div className="d-flex align-items-start gap-3">
              <div
                className="rounded-circle p-2 d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ backgroundColor: "rgba(10, 61, 46, 0.1)", color: "#0A3D2E" }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </div>
              <p className="mb-0 text-secondary small lh-relaxed">
                <strong>How to use:</strong> Select a Sector or Organization, pick your topic of interest, choose the variable(s) to query, and select specific years (or leave default to visualize all recorded years).
              </p>
            </div>
          </div>

          {/* 4-Step Selection Grid */}
          <div className="row g-3 mb-5">
            {/* Column 1: Sector & Organizations */}
            <div className="col-lg-3 col-md-6">
              <div className="bg-white p-3 rounded-4 shadow-sm border h-100">
                <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom d-flex align-items-center gap-2">
                  <span className="badge rounded-circle p-1 bg-light text-dark">1</span>
                  Sectors & Organizations
                </h6>
                <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                  <CofogsAccordion
                    cofogs={cofogs}
                    handlePopulateTopics={handlePopulateTopics}
                    handleSelectOrganisation={handleSelectOrganisation}
                  />
                </div>
              </div>
            </div>

            {/* Column 2: Topic */}
            <div className="col-lg-3 col-md-6">
              <div className="bg-white p-3 rounded-4 shadow-sm border h-100">
                <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom d-flex align-items-center gap-2">
                  <span className="badge rounded-circle p-1 bg-light text-dark">2</span>
                  Select Topic
                </h6>
                <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                  <TopicsAccordion
                    locations={allLocations}
                    loc={loc}
                    handlePopulateVariables={handlePopulateVariables}
                    locationIsLoading={locationIsLoading}
                  />
                </div>
              </div>
            </div>

            {/* Column 3: Data Tables & Variables */}
            <div className="col-lg-3 col-md-6">
              <div className="bg-white p-3 rounded-4 shadow-sm border h-100">
                <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom d-flex align-items-center gap-2">
                  <span className="badge rounded-circle p-1 bg-light text-dark">3</span>
                  Tables & Variables
                </h6>
                <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                  <AccordionComponent
                    dataTables={dataTables}
                    dataTableIsLoading={dataTableIsLoading}
                    handleFetchTableNodes={handleFetchTableNodes}
                    handleFetchTableNodeForVariables={handleFetchTableNodeForVariables}
                    selectedTables={selectedTables}
                    variablesByTable={variablesByTable}
                  />
                </div>
              </div>
            </div>

            {/* Column 4: Years Filter */}
            <div className="col-lg-3 col-md-6">
              <div className="bg-white p-3 rounded-4 shadow-sm border h-100">
                <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom d-flex align-items-center gap-2">
                  <span className="badge rounded-circle p-1 bg-light text-dark">4</span>
                  Filter Years
                </h6>
                <div className="column rounded-3 overflow-y-auto" style={{ maxHeight: "320px", border: "1px solid #E2E8F0" }}>
                  <ul className="list-unstyled mb-0 p-1">
                    {years.length > 0 ? (
                      years.map(year => (
                        <li
                          key={year}
                          className="px-3 py-2 my-1 rounded-2 fw-medium small cursor-pointer transition-all"
                          style={{
                            backgroundColor: selectedYear.includes(+year) ? '#0A3D2E' : 'transparent',
                            color: selectedYear.includes(+year) ? '#ffffff' : '#334155',
                            cursor: 'pointer',
                          }}
                          onClick={() => handleSelectYear(year)}
                        >
                          {year}
                        </li>
                      ))
                    ) : (
                      <li className="px-3 py-4 text-center text-muted small">
                        No years available yet.
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Recharts Chart Visualization Section */}
          {graph.length > 0 && (
            <div className="bg-white p-4 rounded-4 shadow-sm border">
              
              {/* Action Buttons */}
              <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom flex-wrap gap-2">
                <h5 className="fw-bold text-dark mb-0">
                  {selectedTables.join(" + ") || "Data Visualization"}
                </h5>
                <div className="d-flex align-items-center gap-2">
                  <ReactToPrint
                    trigger={() => (
                      <button
                        className="btn text-white rounded-pill px-4 py-2 border-0 small fw-medium d-inline-flex align-items-center gap-2"
                        style={{ backgroundColor: "#0A3D2E", fontSize: "13px" }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 6 2 18 2 18 9"></polyline>
                          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                          <rect x="6" y="14" width="12" height="8"></rect>
                        </svg>
                        <span>Print PDF</span>
                      </button>
                    )}
                    content={() => componentRef.current}
                  />

                  <button
                    onClick={handleDownloadExcel}
                    className="btn border rounded-pill px-4 py-2 small fw-medium d-inline-flex align-items-center gap-2"
                    style={{ color: "#0A3D2E", borderColor: "#0A3D2E", fontSize: "13px" }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <span>Download Excel</span>
                  </button>
                </div>
              </div>

              {/* Printable Chart Container */}
              <div
                className="p-3 rounded-3"
                ref={componentRef}
                style={{
                  background: `url(/assets/img/logo-alt1.png) center center/contain no-repeat`,
                }}
              >
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={graph} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" stroke="#64748B" />
                    <YAxis stroke="#64748B" />
                    <Tooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                    <Legend />
                    {selectedTables.flatMap(table =>
                      variablesByTable[table]?.map(variable => (
                        <Bar
                          key={`${table}-${variable}`}
                          dataKey={variable}
                          fill={generateRandomColor()}
                          name={variable}
                          radius={[4, 4, 0, 0]}
                        />
                      )) || []
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>

            </div>
          )}

        </div>
      </section>

      <ToastContainer closeOnClick pauseOnHover />
      <Footer />
    </div>
  );
};

export default SliceAndDice;